import { Component, OnInit, Input, ViewChild, ElementRef, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, combineLatestAll, map, tap, withLatestFrom } from 'rxjs';

@Component({
    selector: 'dropdown-input',
    templateUrl: './dropdown-input.component.html',
    styleUrls: ['./dropdown-input.component.scss'],
    standalone: false
})
export class DropdownInputComponent implements OnInit {
    // The current value of the input (getter and setter)
    @Input() public set value(value: string | undefined) {
        this._value = value;
        this._currentText = value ?? '';
    }

    public get value(): string {
        return this._value;
    }

    // Event handler for value changes
    @Output() public valueChange = new EventEmitter<string | undefined>();

    public _items$ = new BehaviorSubject<string[]>([]);

    // The items that are selectable within this dropdown
    @Input() public set items(value: string[]) {
        this._items$.next(value);
    }

    // Whether or not this input is readonly
    @Input() public readonly: boolean = false;

    // Whether or not this input can be empty
    @Input() public nullable: boolean = false;

    // Event handlers
    @Output('focus') public onFocus = new EventEmitter<FocusEvent>();
    @Output('blur') public onBlur = new EventEmitter<void>();
    @Output('keydown') public onKeydown = new EventEmitter<KeyboardEvent>();

    // Reference to the input element
    @ViewChild('input', { static: true }) public _inputElement: ElementRef;

    // Holding the current filtered items
    // private _filteredItems: string[] = [];
    public _open: boolean = false;
    public hoverIndex: number = null;

    public _value: string = '';
    private _currentText: string = '';
    private _filterText$ = new BehaviorSubject<string>('');

    constructor(private changeDetectorRef: ChangeDetectorRef) { }

    public ngOnInit() {
        // Subscribe to events
        this.keydownEvents$.pipe(
            withLatestFrom(this._filteredItems$)
        )
            .subscribe(([event, items]) => {
                this.keyDownHandler(event, items);
            });

        this.blurEvents$.pipe(
            withLatestFrom(this._filteredItems$)
        ).subscribe(([, items]) => this.blurHandler(items));
    }

    /**
     * Focuses the input's content
     * @returns void
     */
    public focus(): void {
        this._inputElement.nativeElement.focus();
    }

    /**
     * Blurs the input's content (if it has focus)
     * @returns void
     */
    public blur(): void {
        // Blur input
        this._inputElement.nativeElement.blur();

        // Close
        this._open = false;
    }

    /**
     * Selects the input's content
     * @returns void
     */
    public select(): void {
        this._inputElement.nativeElement.select();
    }

    /* --- Event handlers --- */

    public _handleValueChange(value: string) {
        // Update current text
        this._currentText = value;

        // Update filter
        this._filterText$.next(value);
    }

    public _handleClick(event: MouseEvent) {
        setTimeout(() => {
            // Show the dropdown list
            this._open = true;

            // Emit focus event
            this.onFocus.emit(event);
        });
    }

    public _handleFocus(event: FocusEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    private blurEvents$ = new Subject<void>();

    private async blurHandler(filteredItems: string[]) {
        if (this._currentText !== this._value) {
            if (!this._currentText && this.nullable) {
                // Assign empty
                this.value = undefined;

                // Trigger change event
                this.valueChange.emit(this.value);
            } else {
                // If there is a value in the list that matches the text exactly, value becomes that item, else return to previous value
                const match = filteredItems.find(i => i.toLowerCase().startsWith(this._currentText.toLowerCase()));

                if (!!match) {
                    // Get the only item remaining
                    const newValue = match;

                    // Clear the content if required
                    if (this.value === newValue)
                        await this.clearValue();

                    // Assign the value
                    this.value = newValue;

                    // Trigger change event
                    this.valueChange.emit(this.value);
                } else {
                    // Store the previous text
                    const currentValue = this._value;

                    // Clear the content
                    await this.clearValue();

                    // Re-assing
                    this.value = currentValue;
                }
            }
        }

        // Close the dropdown list
        this._open = false;
        this.changeDetectorRef.detectChanges();

        // Emit blur event
        this.onBlur.emit();
    }

    public async _handleBlur() {
        this.blurEvents$.next();
    }

    /**
     * Incorporating reactive approach
     * @date 5/26/2023 - 3:32:35 PM
     *
     * @type {*}
     */
    private keydownEvents$ = new Subject<KeyboardEvent>();

    private keyDownHandler(event: KeyboardEvent, _filteredItems: string[]) {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

        // Prevent default and stop further propagation
        event.preventDefault();
        event.stopPropagation();

        if (event.key === 'ArrowDown') {
            this.hoverIndex = this.hoverIndex != null ? Math.min(this.hoverIndex + 1, _filteredItems.length - 1) : 0;
        } else if (event.key === 'ArrowUp') {
            this.hoverIndex = this.hoverIndex != null ? Math.max(this.hoverIndex - 1, 0) : _filteredItems.length - 1;
        }

        // Change the value
        this.value = _filteredItems[this.hoverIndex];

        // Emit value change event
        this.valueChange.emit(this.value);
    }

    public _handleKeydown(event: KeyboardEvent) {
        this.keydownEvents$.next(event);
    }

    public _selectItem(item: string) {
        // Assign the correct value
        this.value = item;

        // Blur the input
        this.blur();

        // Emit value change
        this.valueChange.emit(this.value);
    }

    public _handleMouseEnterItem(index: number) {
        this.hoverIndex = index;
    }

    public _handleMouseOutItem() {
        this.hoverIndex = null;
    }

    public _purgeEvent(event: Event) {
        event.stopPropagation();
    }

    /* --- Helper functions --- */
    public _filteredItems$ = combineLatest([this._items$, this._filterText$])
        .pipe(
            map(([items, text]) =>
                // Check if filter will have matches
                (items ?? []).some(item => item.toLowerCase().includes(text.toLowerCase()))
                    // No matches => show whole list
                    ? items ?? []
                    // Or show matches
                    : (items ?? []).filter(item => item.toLowerCase().includes(text.toLowerCase()))

            )
        );

    private async clearValue() {
        this.value = this.value === null ? '' : null;
        return new Promise(resolve => {
            setTimeout(resolve, 0);
        });
    }
}
